import { Log } from '@/logging/datadog';
import { isValidKennitala } from '@/utils/utils';

export const inputKennitalaId = 'input-kennitala';
export const inputNetfangId = 'input-netfang';
export const inputSimiId = 'input-simi';
export const inputSkilabodId = 'input-skilabod';
export const inputNafnId = 'input-nafn';
export const checkboxEmailId = 'checkbox-email';
export const checkboxSimiId = 'checkbox-simi';
export const dropdownErindiId = 'dropdown-erindi';

const salaOgThjonusta = 'Sölu og þjónusturáðgjöf';
const simi = 'Sími';
const taekniAdstod = 'Tæknileg aðstoð';
const reiningar = 'Reikningar';
const vefVerslun = 'Vefverslun';

const salaOgThjonustaFyrirtaekja = 'Sölu og þjónusturáðgjöf fyrirtækja';
const taekniAdstodFyrirtaekja = 'Tæknileg aðstoð fyrirtækja';
const reikningarOgAlmennFyrirtaekjathjonusta = 'Reikningar og almenn fyrirtækjaþjónusta';

export type ContactUsTab = 'einstaklingar' | 'fyrirtaeki';

export const einstaklingarDropdownValues = [
  { value: salaOgThjonusta, label: salaOgThjonusta },
  { value: simi, label: simi },
  { value: taekniAdstod, label: taekniAdstod },
  { value: reiningar, label: reiningar },
  { value: vefVerslun, label: vefVerslun },
];

export const fyrirtaekiDropdownValues = [
  { value: salaOgThjonustaFyrirtaekja, label: salaOgThjonustaFyrirtaekja },
  { value: taekniAdstodFyrirtaekja, label: taekniAdstodFyrirtaekja },
  { value: reikningarOgAlmennFyrirtaekjathjonusta, label: reikningarOgAlmennFyrirtaekjathjonusta },
];

export const getDropdownValues = (tab: ContactUsTab) =>
  tab === 'fyrirtaeki' ? fyrirtaekiDropdownValues : einstaklingarDropdownValues;

export const getEndEmail = (issue: string, getPhoneCall: boolean) => {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    return 'gylfisg@vodafone.is';
  } else if (issue === salaOgThjonusta) {
    return getPhoneCall ? 'pantasimtalreikningar@vodafone.is' : 'vodafone@vodafone.is';
  } else if (issue === simi) {
    return getPhoneCall ? 'pantasimtalfarsimi@vodafone.is' : 'vodafone@vodafone.is';
  } else if (issue === taekniAdstod) {
    return getPhoneCall ? 'pantasimtaltaekni@vodafone.is' : 'hjalp@vodafone.is';
  } else if (issue === reiningar) {
    return getPhoneCall ? 'pantasimtalreikningar@vodafone.is' : 'reikningar@vodafone.is';
  } else if (issue === vefVerslun) {
    return 'vefverslun@vodafone.is';
  } else if (issue === salaOgThjonustaFyrirtaekja) {
    return 'fyrirtaekjasala@syn.is';
  } else if (issue === taekniAdstodFyrirtaekja) {
    return 'taf@syn.is';
  } else if (issue === reikningarOgAlmennFyrirtaekjathjonusta) {
    return 'firma@syn.is';
  }
  return 'vodafone@vodafone.is';
};

const validateForm = (formInputState: Record<string, string>): string => {
  const kennitalaInput = formInputState[inputKennitalaId];

  if (!(kennitalaInput && isValidKennitala(kennitalaInput)))
    return 'Kennitala er ekki á réttu formi. Hún skal vera 10 tölustafir án bandstriks.';

  return '';
};

export const handleSubmit = async (
  formInputState: Record<string, string>,
  checkboxState: Record<string, boolean>,
  selectedDropdownValue: string,
  showThankYouModal: React.Dispatch<React.SetStateAction<boolean>>,
  setErrorState: React.Dispatch<React.SetStateAction<boolean>>,
  setErrorStateText: React.Dispatch<React.SetStateAction<string | null>>,
  setLoadingState: React.Dispatch<React.SetStateAction<boolean>>,
  resetForm: () => void,
  selectedTab: ContactUsTab
) => {
  const validationError = validateForm(formInputState);
  if (validationError && validationError.length > 0) {
    setErrorStateText(validationError);
    setErrorState(true);
    return;
  }

  let preferredCommunicationWay = '';
  if (selectedTab === 'fyrirtaeki') {
    preferredCommunicationWay = 'email';
  } else if (checkboxState[checkboxSimiId]) {
    preferredCommunicationWay = 'phone';
  } else if (checkboxState[checkboxEmailId]) {
    preferredCommunicationWay = 'email';
  }

  try {
    setLoadingState(true);
    const auth = btoa(process.env.NEXT_PUBLIC_API_USER_NAME + ':' + process.env.NEXT_PUBLIC_API_PASSWORD);

    const response = await fetch(`${process.env.NEXT_PUBLIC_VODAFONE_EXPERIENCE_ENDPOINT}/case`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${auth}`,
      },
      body: JSON.stringify({
        suppliedName: formInputState[inputNafnId],
        suppliedEmail: formInputState[inputNetfangId],
        suppliedPhone: formInputState[inputSimiId],
        suppliedNationalId: formInputState[inputKennitalaId],
        subject: selectedDropdownValue,
        description: formInputState[inputSkilabodId],
        preferredCommunication: preferredCommunicationWay,
      }),
    });

    if (response.ok) {
      showThankYouModal(() => true);
      setErrorState(false);
      setLoadingState(false);
      resetForm();
    } else {
      const errorData = await response.json();
      Log.error('Error response:', errorData);
      setErrorState(true);
      setLoadingState(false);
    }
  } catch (error) {
    Log.error('Error sending hafa-samband email', error);
    setErrorState(true);
    setLoadingState(false);
  }
};

export const getReCAPTCHAId = () => {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    /*
      Testing key from ReCAPTCHA
      https://developers.google.com/recaptcha/docs/faq#id-like-to-run-automated-tests-with-recaptcha.-what-should-i-do
    */
    return '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
  } else {
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  }
};
